import { ReactNode } from 'react'
import { immer } from 'zustand/middleware/immer'

import { ToastAndPosition, ToastProps } from '~/astra'
import { getFlag } from '~/hooks/useFlag'

import { Slice, useStore } from '../store'

type State = {
    toasts: ToastAndPosition[]
}

const initialState: State = {
    toasts: [],
}

type Actions = {
    actions: {
        addToast: (toast: ToastAndPosition) => void
        removeToast: (id: string) => void
    }
}

export type ToasterSlice = {
    toaster: State & Actions
}

export const createToasterSlice: Slice<ToasterSlice> = immer(set => ({
    toaster: {
        ...initialState,
        actions: {
            addToast(toast: ToastAndPosition) {
                set(state => {
                    const skipErrors = getFlag('disable_critical_toasts')
                    // DEMO HOTFIX: Temporary fix to prevent multiple critical toasts from being added for demo
                    if (toast.color === 'critical' && skipErrors) {
                        return
                    }
                    state.toaster.toasts = [
                        ...state.toaster.toasts,
                        {
                            placement: toast.placement ?? 'top-right',
                            id: toast.id ?? crypto.randomUUID(),
                            ...toast,
                        },
                    ]
                })
            },
            removeToast(id: ToastProps['id']) {
                set(state => {
                    state.toaster.toasts = state.toaster.toasts.filter(toast => toast.id !== id)
                })
            },
        },
    },
}))

export function dispatchErrorToast(toast: ToastProps) {
    const addToast = useStore.getState().toaster.actions.addToast
    const removeToast = useStore.getState().toaster.actions.removeToast
    const id = crypto.randomUUID()

    addToast({
        ...toast,
        color: 'critical',
        placement: 'top-right',
        onClose: () => removeToast(id),
    })
}

export function dispatchSuccessToast(message: ReactNode) {
    const addToast = useStore.getState().toaster.actions.addToast
    const removeToast = useStore.getState().toaster.actions.removeToast
    const id = crypto.randomUUID()

    addToast({
        message,
        color: 'success',
        placement: 'top-right',
        onClose: () => removeToast(id),
    })
}

export function dispatchDefaultToast(message: ReactNode) {
    const addToast = useStore.getState().toaster.actions.addToast
    const removeToast = useStore.getState().toaster.actions.removeToast
    const id = crypto.randomUUID()

    addToast({
        message,
        color: 'default',
        placement: 'top-right',
        onClose: () => removeToast(id),
    })
}
